import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { BottomSheetModal } from '../../components/BottomSheetModal';

const useStyles = createUseStyles({
    content: {
        padding: '28px 24px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        backgroundColor: '#fff'
    },
    title: {
        fontSize: 20,
        fontWeight: 600,
        color: 'var(--ion-color-dark)',
        marginBottom: 2,
        letterSpacing: 'normal'
    },
    helper: {
        fontSize: 14,
        color: 'var(--ion-color-medium)',
        lineHeight: 1.45,
        marginBottom: 8
    },
    option: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: 14,
        border: '1px solid var(--ion-color-step-100)',
        borderRadius: 10,
        cursor: 'pointer'
    },
    optionSelected: {
        borderColor: 'var(--ion-color-primary)',
        backgroundColor: 'rgba(17, 120, 157, 0.06)'
    },
    dot: {
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: '2px solid var(--ion-color-step-150)',
        marginTop: 2,
        flexShrink: 0,
        background: '#fff'
    },
    dotSelected: {
        borderColor: 'var(--ion-color-primary)',
        boxShadow: 'inset 0 0 0 4px var(--ion-color-primary)'
    },
    optionTitle: {
        fontWeight: 600,
        fontSize: 15,
        color: 'var(--ion-text-color)'
    },
    optionDescription: {
        fontSize: 13,
        color: 'var(--ion-color-medium)',
        marginTop: 2,
        lineHeight: 1.45
    }
});

export interface ISharingSettingsSheetProps {
    isOpen: boolean;
    onDidDismiss: () => void;
}

export const SharingSettingsSheet: FC<ISharingSettingsSheetProps> = ({ isOpen, onDidDismiss }) => {
    const classes = useStyles();
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const caremap = careMapState.getActiveCaremap();
    const patient = caremap?.patient;

    if (!patient) {
        return null;
    }

    const isShared = patient.notesIsPublic === true;

    const select = (shared: boolean) => {
        if (shared !== isShared) {
            mixpanel.track('Care Recipient Visibility Default Changed', () => ({
                'State': shared ? 'shared' : 'private'
            }));
            careMapState.updatePatient({ ...patient, notesIsPublic: shared });
        }
        onDidDismiss();
    };

    return (
        <BottomSheetModal
            isOpen={isOpen}
            onDidDismiss={onDidDismiss}
            data-id="sharing-settings-sheet"
        >
            <div className={classes.content}>
                <div className={classes.title}>Sharing Settings</div>
                <div className={classes.helper}>
                    For new notes and recordings on this care recipient. You can change visibility per note.
                </div>

                <div
                    className={`${classes.option} ${isShared ? classes.optionSelected : ''}`}
                    onClick={() => select(true)}
                    data-id="sharing-settings-shared"
                >
                    <div className={`${classes.dot} ${isShared ? classes.dotSelected : ''}`} />
                    <div>
                        <div className={classes.optionTitle}>Shared with viewers</div>
                        <div className={classes.optionDescription}>
                            New notes and recordings will be visible to invited viewers, plus you and any co-owners.
                        </div>
                    </div>
                </div>

                <div
                    className={`${classes.option} ${!isShared ? classes.optionSelected : ''}`}
                    onClick={() => select(false)}
                    data-id="sharing-settings-private"
                >
                    <div className={`${classes.dot} ${!isShared ? classes.dotSelected : ''}`} />
                    <div>
                        <div className={classes.optionTitle}>Hidden from viewers</div>
                        <div className={classes.optionDescription}>
                            New notes and recordings will be private to you and any co-owners. You choose what to share with viewers.
                        </div>
                    </div>
                </div>
            </div>
        </BottomSheetModal>
    );
};
